import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isContactPage = location.pathname === '/contact';
  const isDarkPage = isContactPage;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const textColor = isDarkPage && !isScrolled ? 'text-white' : 'text-gray-900';
  const textColorMuted = isDarkPage && !isScrolled ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-sm' 
          : isDarkPage 
            ? 'bg-transparent' 
            : 'bg-transparent'
      }`}
      data-testid="header"
    >
      <nav className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => handleNavigation('/')}
            className={`text-xl font-black tracking-tight transition-colors duration-300 ${
              isScrolled ? 'text-gray-900 hover:text-gray-700' : `${textColor} hover:opacity-80`
            }`}
            data-testid="logo-btn"
          >
            SHAHRUKH
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <button
              onClick={() => handleNavigation('/')}
              className={`text-sm font-medium transition-colors duration-300 ${
                isScrolled ? 'text-gray-700 hover:text-gray-900' : textColorMuted
              } ${location.pathname === '/' ? 'font-semibold' : ''}`}
              data-testid="nav-home"
            >
              Home
            </button>
            <button
              onClick={() => handleNavigation('/about')}
              className={`text-sm font-medium transition-colors duration-300 ${
                isScrolled ? 'text-gray-700 hover:text-gray-900' : textColorMuted
              } ${location.pathname === '/about' ? 'font-semibold' : ''}`}
              data-testid="nav-about"
            >
              About
            </button>
            <button
              onClick={() => handleNavigation('/insights')}
              className={`text-sm font-medium transition-colors duration-300 ${
                isScrolled ? 'text-gray-700 hover:text-gray-900' : textColorMuted
              } ${location.pathname === '/insights' ? 'font-semibold' : ''}`}
              data-testid="nav-insights"
            >
              Insights
            </button>
            <Button
              onClick={() => handleNavigation('/contact')}
              size="sm"
              className={`transition-all duration-300 ${
                isScrolled || !isDarkPage
                  ? 'bg-gray-900 text-white hover:bg-gray-800'
                  : 'bg-white text-gray-900 hover:bg-gray-100'
              }`}
              data-testid="nav-contact"
            >
              Contact
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden transition-colors duration-300 ${
              isScrolled ? 'text-gray-900' : textColor
            }`}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className={`md:hidden mt-4 pb-4 space-y-3 ${
            isDarkPage && !isScrolled ? 'bg-gray-900/95 -mx-6 px-6 py-4 rounded-lg backdrop-blur-md' : ''
          }`}>
            <button
              onClick={() => handleNavigation('/')}
              className={`block w-full text-left text-sm font-medium py-2 transition-colors ${
                isDarkPage && !isScrolled ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              Home
            </button>
            <button
              onClick={() => handleNavigation('/about')}
              className={`block w-full text-left text-sm font-medium py-2 transition-colors ${
                isDarkPage && !isScrolled ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              About
            </button>
            <button
              onClick={() => handleNavigation('/insights')}
              className={`block w-full text-left text-sm font-medium py-2 transition-colors ${
                isDarkPage && !isScrolled ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              Insights
            </button>
            <Button
              onClick={() => handleNavigation('/contact')}
              size="sm"
              className="w-full bg-gray-900 text-white hover:bg-gray-800"
            >
              Contact
            </Button>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;